import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { UserService } from 'src/app/services/user.service';
import { ConfigService } from 'src/app/services/config.service';
import { Router } from '@angular/router';
import { HistoryItem, HistoryItemType } from '../../../models/history-user';
import { Record } from 'src/app/models/record';
import { ApiService } from 'src/app/services/api.service';
import { RecordRequest } from 'src/app/models/record-request';
import { CustomerType } from '../../../models/client-configuration';
import { SearchModel } from 'src/app/models/search-model';
import { combineLatest } from 'rxjs';
import { UserHistoryService } from './service/user-history.service';
import { ConsumptionTimeRequest } from './models/consumption-time-request.model';

@Component({
  selector: 'app-user-history',
  templateUrl: './user-history.component.html',
  styleUrls: ['./user-history.component.scss'],
})
export class UserHistoryComponent implements OnInit {
  @Output()
  submitted: EventEmitter<boolean> = new EventEmitter();

  section = 'history';
  private readonly PAGE_SIZE = 10;

  waiting = false;
  isLastPage = true;
  page = 0;
  corporateOrAcademicCustomer: boolean;
  customerType: CustomerType;

  isLoading = true;

  showEnableUserHistoryExport: boolean;

  constructor(
    public userService: UserService,
    private apiService: ApiService,
    private configService: ConfigService,
    private router: Router,
    private userHistoryService: UserHistoryService
  ) {}

  ngOnInit() {
    if (this.configService.get().showUserHistory) {
      this.init();
    } else {
      this.router.navigate(['/user/checkouts']);
    }
  }

  private init() {
    this.showEnableUserHistoryExport = this.configService.get().enableUserHistoryExport;
    this.customerType = this.configService.customerType;
    this.corporateOrAcademicCustomer = ['Corporate', 'Academic'].includes(this.customerType.dbValue);
    if (this.userService.history.items.length) {
      this.isLastPage = this.userService.history.lastPage;
      this.isLoading = false;
    } else {
      this.loadHistory(this.page);
    }
  }

  private loadHistory(page: number) {
    this.userService.getUserHistory(page, this.PAGE_SIZE).subscribe({
      next: (history) => {
        if (history.items.length === 0) {
          this.isLoading = false;
          this.waiting = false;
          this.isLastPage = true;
          return;
        }

        const recordIds = history.items.map((item) => item.recordId);
        const recordRequest: RecordRequest = {
          recordIds: recordIds,
          availability: true,
          allRecords: true,
        };

        this.apiService.getRecords(recordRequest).subscribe((result) => {
          result.records.forEach((record) => {
            const historyItem = history.items.find((item) => item.recordId === record.id);
            if (historyItem) {
              this.addRecordToHistoryItem(historyItem, record);
            }
            this.userService.history.items.push(historyItem);
          });
          this.isLoading = false;
          this.waiting = false;
          this.isLastPage = history.lastPage;
          this.userService.history.lastPage = history.lastPage;
          this.page = page + 1;

          const consumptionTimeRequest = history.items.map(({ recordId, learningExperience }) => ({
            recordId,
            isLearningExperience: learningExperience,
          }));

          this.loadProgressAndConsumptionTime(page, consumptionTimeRequest);
        });
      },
      error: (error) => {
        this.isLoading = false;
      }
    });
  }

  private loadProgressAndConsumptionTime(page: number, consumptionTimeRequest: ConsumptionTimeRequest[]) {
    const startDate = this.getOldestCheckoutStartDate();
    combineLatest(
      this.apiService.getHistoryResourcesProgress(page, this.PAGE_SIZE),
      this.apiService.getConsumptionTime(this.userService.getUserId(), consumptionTimeRequest, startDate)
    ).subscribe(
      ([progresses, timeReadings]) => {
        progresses.forEach((progress) => {
          this.userService.history.items.forEach((historyItem) => {
            if (historyItem.recordId === progress.nInf && historyItem.percentage === null) {
              historyItem.percentage = progress.percentage;
            }
          });
        });

        if (this.corporateOrAcademicCustomer && timeReadings) {
          timeReadings.forEach((timeReading) => {
            this.userService.history.items.forEach((historyItem) => {
              if (historyItem.recordId === timeReading.recordId) {
                if (historyItem.type === HistoryItemType.PRESTAMO_REMOTO) {
                  historyItem.ebookTimeReading = this.milisecondsToString(Number(historyItem.ebookTimeReading));
                } else {
                  historyItem.ebookTimeReading = this.milisecondsToString(timeReading.ebookTimeReading);
                }
              }
            });
          });
        }
        this.isLoading = false;
        this.userHistoryService.loadMoreItems.next(true);
      },
      () => {
        this.isLoading = false;
      }
    );
  }

  private addRecordToHistoryItem(historyItem: HistoryItem, record: Record) {
    historyItem.record = record;
  }

  loadMore() {
    if (this.waiting) {
      return;
    }
    this.waiting = true;
    this.loadHistory(this.page);
  }

  onRemoved(historyItem: HistoryItem) {
    this.userService.removeItemFromHistory(historyItem);
  }

  explore() {
    const model = new SearchModel(undefined, this.configService.get().resultsFilterOrder);
    model.explore = true;

    this.submitted.emit(true);
    this.router.navigate(['/results'], {
      queryParams: model.toRequest(),
    });
  }

  private milisecondsToString(miliseconds: number) {
    const seconds = miliseconds / 1000;
    const segundos = Math.round(seconds % 0x3c);
    const horas = Math.floor(seconds / 0xe10);
    const minutos = Math.floor(seconds / 0x3c) % 0x3c;

    return this.normalizeNumber(horas) + ':' + this.normalizeNumber(minutos) + ':' + this.normalizeNumber(segundos);
  }

  private normalizeNumber(number: number): string {
    if (number === 0 || Number.isNaN(number)) {
      return '00';
    } else if (number < 10) {
      return '0' + number.toString();
    } else {
      return number.toString();
    }
  }

  private getOldestCheckoutStartDate(): Date {
    let oldestDate: Date;
    this.userService.history.items.forEach((item) => {
      if ((oldestDate == undefined || item.start < oldestDate) && item.start != null) {
        oldestDate = item.start;
      }
    });

    return oldestDate;
  }
}
